import type { PropsWithChildren, FC } from 'react'
import { useState } from 'react'
import { useCheckLaunchOnboardingProgress } from '../../../../../utils/onboarding/useCheckLaunchOnboardingProgress.hooks'
import { useCurrentAccountContext } from '../../../../../utils/current-account/CurrentAccountContext'
import { usePartnerContext } from '../../../../../utils/contexts/partner/PartnerContext.hooks'
import { useUserSettingsHierarchy } from '../../NavMenuHierarchy'
import { useNavContext } from '../../../nav/utils/NavContext'
import { PartnerType } from '../../../../../exports/utils'
import { useTrackEvent } from '../../../../../utils/events/useTrackEvent.hook'
import { AdminControlModal } from '../../../admin-controls-modal/AdminControlsModal'
import { CollapsibleOption } from './CollapsibleOption'
import { Option } from './Option'
import { UserMenu } from './UserMenu'
import { Divider, TitleText } from './UserSettingsMenu.common'
import { SubOption } from './SubOption'

type Props = {
  setShowPartnerSelector: (value: boolean) => void
  setCurrentlySelectedItem: (value: number) => void
  setShowManageAccount: (value: boolean) => void
}

export const UserSettingsMenuRetailer: FC<PropsWithChildren<Props>> = ({
  setShowPartnerSelector,
  setCurrentlySelectedItem,
  setShowManageAccount,
}) => {
  const [currentlyOpenSubMenu, setCurrentlyOpenSubMenu] = useState(-1)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const context = useCurrentAccountContext()
  const partnerContext = usePartnerContext()
  const { hasInProgressLaunchOnboarding } = useCheckLaunchOnboardingProgress()
  const { setMostRecentClickedRoute } = useNavContext()
  const { navMenuHierarchy: userSettingsHierarchy } = useUserSettingsHierarchy(setShowManageAccount)

  const showSettings = partnerContext.partnerType !== PartnerType.IDP

  const onClose = () => {
    setIsOpen(false)
    // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
    setMostRecentClickedRoute(null)
  }

  const trackEvent = useTrackEvent()

  return (
    <UserMenu
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      setCurrentlySelectedItem={setCurrentlySelectedItem}
      // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
      title={partnerContext.partnerName}
      navMenuHierarchy={userSettingsHierarchy}
    >
      <AdminControlModal />
      <div>
        <TitleText padding={'8px 24px'}>{partnerContext.partnerName}</TitleText>
        <Option
          onClick={() => {
            setShowPartnerSelector(true)
            onClose()
          }}
          title="navV2.userSettings.switchPartner"
        />
        {hasInProgressLaunchOnboarding && (
          <Option
            onClick={onClose}
            route="onboarding-launch-checklist"
            title="onboarding.launchChecklist"
          />
        )}
        <Divider />
        {userSettingsHierarchy.hierarchy
          .filter(section => (showSettings || !section.isSettings) && !section.isUserActions)
          .map((section, index) => (
            <CollapsibleOption
              key={section.name}
              index={index}
              currentlyOpen={currentlyOpenSubMenu}
              setCurrentlyOpen={setCurrentlyOpenSubMenu}
              title={section.name}
              onClose={onClose}
            >
              {section.children?.map((child, idx) => (
                <SubOption
                  key={idx}
                  onClick={() => {
                    child.onClick?.()
                    onClose()
                  }}
                  title={child.name}
                  route={child.route}
                  position={child.positionNavV2}
                  accessControl={child.accessControl}
                />
              ))}
            </CollapsibleOption>
          ))}
        <Divider />
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            padding: '12px',
            paddingLeft: '27px',
          }}
        >
          <TitleText fontWeight={600} fontSize="14px" lineHeight="20px">
            {context.account.givenName}
          </TitleText>
          <TitleText fontWeight={400} fontSize="12px" lineHeight="16px">
            {context.account.email}
          </TitleText>
        </div>

        {userSettingsHierarchy.hierarchy
          .filter(section => section.isUserActions)
          .map(section =>
            section.children?.map((item, idx) => (
              <Option
                key={idx}
                onClick={() => {
                  item.onClick?.()
                  onClose()
                }}
                route={item.route}
                title={item.name}
                accessControl={item.accessControl}
              />
            ))
          )}
      </div>
    </UserMenu>
  )
}
